import { useCallback } from 'react'
import { MessageSquare } from 'lucide-react'
import { useGetRealChatId } from '@renderer/chat'
import { EmptyState, EmptyStateDescription, EmptyStateIcon } from '../../../components/meeting/empty-state'
import { ConversationChatThread } from '../../../components/chat/conversation-chat-drawer'
import { useViewActions } from '../../../view-atoms'
import { useViewContext, useViewEntranceSettled } from '../../../view-context'
import { useSettleMeetingChatFreshSubmit } from '../use-meeting-chat-drawer'
import type { MeetingViewContext, MeetingViewModel } from '../use-meeting-view-model'

/**
 * The meeting's Chat tab: its thread, live or after it ends. While recording,
 * each send carries only the transcript said since the last one (see
 * `buildLiveTranscriptText`).
 */
export function MeetingViewChatPanel({ model }: { model: Pick<MeetingViewModel, 'variant'> }) {
  const { context } = useViewContext<MeetingViewContext>()
  const getRealChatId = useGetRealChatId()
  const chatId = context?.chatId ? getRealChatId(context.chatId) : undefined
  const entranceSettled = useViewEntranceSettled()
  const settleFreshSubmit = useSettleMeetingChatFreshSubmit()
  const { openView } = useViewActions()
  const handleStagedActionClick = useCallback(
    (actionId: string) => openView('action', { actionId }, { sourceLabel: 'Meeting' }),
    [openView],
  )

  if (!chatId) {
    return (
      <section aria-label="Chat" className="flex flex-col px-5 pb-5 pt-1">
        <EmptyState className="py-6">
          <EmptyStateIcon>
            <MessageSquare />
          </EmptyStateIcon>
          <EmptyStateDescription>
            {model.variant === 'live'
              ? 'Ask about the meeting as it happens. Each message shares the latest transcript.'
              : 'Ask anything about this meeting.'}
          </EmptyStateDescription>
        </EmptyState>
      </section>
    )
  }

  return (
    <section aria-label="Chat" className="relative flex min-h-0 flex-1 flex-col">
      <ConversationChatThread
        chatId={chatId}
        open
        entranceSettled={entranceSettled}
        isFreshSubmit={context?.isFreshSubmit}
        onFreshSubmitSettled={settleFreshSubmit}
        onStagedActionClick={handleStagedActionClick}
        sourceLabel="Meeting"
      />
    </section>
  )
}
